import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../../services/axios';
import GlobalPagination from '../ui/GlobalPagination';
import { formatPhoneCompact } from '../../utils/phoneUtils';

const PAGE_SIZE_OPTIONS = [5, 10, 20];
const DEFAULT_PAGE_SIZE = 20;

const normalizePageSize = (size) => (
    PAGE_SIZE_OPTIONS.includes(Number(size)) ? Number(size) : DEFAULT_PAGE_SIZE
);

const CeipalImportModal = ({ show, onHide, onImportResumes, positionId, prefetchedJobPostings, onPrefetchConsumed }) => {
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1);
    const [jobPostings, setJobPostings] = useState([]);
    const [selectedJob, setSelectedJob] = useState(null);
    const [applicants, setApplicants] = useState([]);
    const [selectedApplicants, setSelectedApplicants] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [error, setError] = useState(null);
    const [importing, setImporting] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalJobs, setTotalJobs] = useState(0);
    const [applicantCurrentPage, setApplicantCurrentPage] = useState(1);
    const [applicantTotalPages, setApplicantTotalPages] = useState(1);
    const [totalApplicants, setTotalApplicants] = useState(0);
    const [jobsPageSize, setJobsPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [applicantsPageSize, setApplicantsPageSize] = useState(DEFAULT_PAGE_SIZE);
    const usedPrefetchRef = useRef(false);

    useEffect(() => {
        if (show) {
            setStep(1);
            setSearchTerm('');
            setSelectedJob(null);
            setApplicants([]);
            setSelectedApplicants([]);
            setError(null);
            setJobsPageSize(DEFAULT_PAGE_SIZE);
            setApplicantsPageSize(DEFAULT_PAGE_SIZE);
            const prefetched = prefetchedJobPostings?.success && (prefetchedJobPostings.data?.length !== undefined);
            if (prefetched) {
                setJobPostings(prefetchedJobPostings.data || []);
                if (prefetchedJobPostings.totalPages != null) setTotalPages(prefetchedJobPostings.totalPages);
                if (prefetchedJobPostings.totalCount != null) setTotalJobs(prefetchedJobPostings.totalCount);
                setCurrentPage(1);
                setLoading(false);
                usedPrefetchRef.current = true;
                if (typeof onPrefetchConsumed === 'function') onPrefetchConsumed();
            } else {
                usedPrefetchRef.current = false;
                loadJobPostings();
            }
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [show]);

    useEffect(() => {
        if (step !== 1) return;
        if (searchTerm === '' && usedPrefetchRef.current) {
            usedPrefetchRef.current = false;
            return;
        }
        const debounceTimer = setTimeout(() => {
            loadJobPostings(1, searchTerm);
        }, 300);
        return () => clearTimeout(debounceTimer);
    }, [searchTerm, step]);

    useEffect(() => {
        if (step !== 2 || !selectedJob) return;
        const debounceTimer = setTimeout(() => {
            setApplicantCurrentPage(1);
            loadApplicantsForJob(selectedJob.id, 1, searchTerm, applicantsPageSize);
        }, 300);
        return () => clearTimeout(debounceTimer);
    }, [searchTerm, step, selectedJob]);

    const loadJobPostings = async (page = 1, search = '', pageSize = jobsPageSize) => {
        setLoading(true);
        setError(null);
        try {
            const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
            const response = await api.get(`/ceipal/job-postings?page=${page}&pageSize=${pageSize}${searchParam}`);
            if (response.data.success) {
                setJobPostings(response.data.data || []);
                if (response.data.totalPages) setTotalPages(response.data.totalPages);
                if (response.data.totalCount) setTotalJobs(response.data.totalCount);
                setCurrentPage(page);
            } else {
                setError(response.data.error || 'Failed to load job postings');
            }
        } catch (err) {
            setError('Failed to load job postings: ' + (err.response?.data?.error || err.message));
        } finally {
            setLoading(false);
        }
    };

    const loadApplicantsForJob = async (jobId, page = 1, search = '', pageSize = applicantsPageSize) => {
        const effectivePageSize = normalizePageSize(pageSize);
        setLoading(true);
        setError(null);
        try {
            const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
            const response = await api.get(`/ceipal/applicants/${jobId}?page=${page}&pageSize=${effectivePageSize}${searchParam}`);
            if (response.data.success) {
                setApplicants(response.data.data || []);
                if (response.data.totalPages != null) setApplicantTotalPages(response.data.totalPages);
                if (response.data.totalCount != null) setTotalApplicants(response.data.totalCount);
                setApplicantCurrentPage(page);
                if (response.data.pageSize != null) {
                    setApplicantsPageSize(normalizePageSize(response.data.pageSize));
                } else {
                    setApplicantsPageSize(effectivePageSize);
                }
            } else {
                setError(response.data.error || 'Failed to load applicants');
            }
        } catch (err) {
            setError('Failed to load applicants: ' + (err.response?.data?.error || err.message));
        } finally {
            setLoading(false);
        }
    };

    const handleJobSelection = (job) => {
        setSelectedJob(job);
        setSelectedApplicants([]);
        setSearchTerm('');
        setStep(2);
    };

    const handleApplicantSelection = (applicantId, isSelected) => {
        if (isSelected) {
            setSelectedApplicants([...selectedApplicants, applicantId]);
        } else {
            setSelectedApplicants(selectedApplicants.filter(id => id !== applicantId));
        }
    };

    const handleSelectPage = (isSelected) => {
        const pageIds = applicants.map(a => a.id);
        if (isSelected) {
            setSelectedApplicants(prev => [...new Set([...prev, ...pageIds])]);
        } else {
            const pageIdSet = new Set(pageIds);
            setSelectedApplicants(prev => prev.filter(id => !pageIdSet.has(id)));
        }
    };

    const handleSelectAllApplicants = async () => {
        if (!selectedJob?.id || totalApplicants === 0) return;
        setLoading(true);
        setError(null);
        try {
            const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : '';
            const response = await api.get(
                `/ceipal/applicants/${selectedJob.id}?page=1&pageSize=${totalApplicants}${searchParam}`
            );
            if (response.data.success) {
                const allIds = (response.data.data || []).map(a => a.id);
                setSelectedApplicants([...new Set(allIds)]);
            } else {
                setError(response.data.error || 'Failed to select all applicants');
            }
        } catch (err) {
            setError('Failed to select all applicants: ' + (err.response?.data?.error || err.message));
        } finally {
            setLoading(false);
        }
    };

    const handleClearAllSelections = () => {
        setSelectedApplicants([]);
    };

    const handleImport = async () => {
        if (!selectedJob || selectedApplicants.length === 0) {
            setError('Please select a job and at least one applicant to import.');
            return;
        }
        setImporting(true);
        setError(null);
        try {
            const applicantIds = [...new Set(selectedApplicants.map(id => String(id)))];
            const response = await api.post('/ceipal/import-resumes', applicantIds, {
                params: { positionId },
                timeout: 300000,
            });
            if (response.data.success) {
                const resumes = response.data.resumes || [];
                const failedCount = response.data.failed_count ?? 0;
                const totalRequested = response.data.total_requested ?? applicantIds.length;
                onImportResumes(resumes);
                if (failedCount > 0) {
                    const errors = response.data.errors || [];
                    const firstReasons = errors.slice(0, 3).map(e => e.message || e.applicant_id).join('; ');
                    setError(`Imported ${resumes.length} of ${totalRequested} resumes. ${failedCount} failed. ${firstReasons}`);
                    setTimeout(() => { setError(null); handleClose(); }, 6000);
                } else {
                    handleClose();
                }
            } else {
                setError(response.data.error || 'Failed to import resumes');
            }
        } catch (err) {
            setError('Failed to import resumes: ' + (err.response?.data?.error || err.message));
        } finally {
            setImporting(false);
        }
    };

    const handleClose = () => {
        setStep(1);
        setSelectedJob(null);
        setSelectedApplicants([]);
        setSearchTerm('');
        setError(null);
        onHide();
    };

    const jobsStartIndex = totalJobs > 0 ? (currentPage - 1) * jobsPageSize + 1 : (jobPostings.length === 0 ? 0 : 1);
    const jobsEndIndex = totalJobs > 0 ? Math.min(currentPage * jobsPageSize, totalJobs) : jobPostings.length;
    const applicantsStartIndex = totalApplicants > 0 && applicants.length > 0
        ? (applicantCurrentPage - 1) * applicantsPageSize + 1
        : 0;
    const applicantsEndIndex = totalApplicants > 0 && applicants.length > 0
        ? Math.min((applicantCurrentPage - 1) * applicantsPageSize + applicants.length, totalApplicants)
        : 0;

    if (!show) return null;

    const footBtn = (grad, shadow) => ({
        padding: '7px 16px', fontSize: 12, fontWeight: 600, borderRadius: 8,
        border: 'none', cursor: 'pointer', fontFamily: 'inherit',
        background: grad, color: '#fff',
        boxShadow: shadow, transition: 'all 0.18s', outline: 'none',
        display: 'inline-flex', alignItems: 'center', gap: 6,
    });

    const thStyle = {
        padding: '8px 10px', fontSize: 11, fontWeight: 700,
        color: 'var(--ds-text2)', background: 'var(--ds-card2)',
        borderBottom: '1px solid var(--ds-border)',
        position: 'sticky', top: 0, zIndex: 1,
        whiteSpace: 'nowrap',
    };

    const tdStyle = {
        padding: '7px 10px', fontSize: 12, color: 'var(--ds-text)',
        borderBottom: '1px solid var(--ds-table-border)',
        verticalAlign: 'middle',
    };

    return createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 12px', paddingLeft: 'calc(var(--app-sidebar-width, 0px) + 12px)' }}>
            {/* Backdrop */}
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)' }} onClick={handleClose} />

            {/* Dialog */}
            <div style={{
                position: 'relative', width: '100%', maxWidth: 820,
                background: 'var(--ds-card)', borderRadius: 12,
                boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
                display: 'flex', flexDirection: 'column',
                maxHeight: 'calc(100vh - 48px)', overflow: 'hidden',
            }}>
                {/* Header */}
                <div style={{ padding: '12px 16px', background: 'linear-gradient(135deg,#0041C2,#1A5FD4)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <i className="fas fa-download" style={{ color: '#fff', fontSize: 13 }} />
                        </div>
                        <div>
                            <div style={{ fontWeight: 700, fontSize: 14, color: '#fff' }}>Import Resumes from CEIPAL</div>
                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', marginTop: 1 }}>
                                {step === 1 ? 'Step 1 of 2 — Select a job posting' : 'Step 2 of 2 — Select applicants to import'}
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {step === 2 && (
                            <button
                                onClick={() => { setStep(1); setSearchTerm(''); }}
                                style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', outline: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                            >
                                <i className="fas fa-arrow-left" style={{ fontSize: 11 }} /> Back
                            </button>
                        )}
                        <button
                            onClick={handleClose}
                            style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.2)', color: '#fff', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', outline: 'none' }}
                        >
                            ×
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '16px', background: 'var(--ds-input-bg)', display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {/* Error alert */}
                    {error && (
                        <div style={{ padding: '10px 14px', background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)', borderRadius: 8, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                            <i className="fas fa-exclamation-circle" style={{ color: '#dc2626', fontSize: 14, flexShrink: 0, marginTop: 1 }} />
                            <span style={{ fontSize: 12, color: '#dc2626', flex: 1 }}>{error}</span>
                            <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 14, padding: 0, lineHeight: 1 }}>×</button>
                        </div>
                    )}

                    {/* Loading */}
                    {loading && (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
                            <div className="spinner-border" style={{ color: '#0041C2', width: 32, height: 32 }} role="status" />
                            <p style={{ marginTop: 12, color: 'var(--ds-text2)', fontSize: 13, marginBottom: 0 }}>Loading...</p>
                        </div>
                    )}

                    {/* ── Step 1: Job Selection ── */}
                    {step === 1 && !loading && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--ds-text)' }}>Select Job Posting</span>
                                <span style={{ padding: '3px 10px', borderRadius: 6, background: 'rgba(0,65,194,0.12)', color: '#1A5FD4', fontSize: 11, fontWeight: 600, border: '1px solid rgba(0,65,194,0.2)' }}>
                                    {totalJobs > 0 ? totalJobs : jobPostings.length} jobs
                                </span>
                            </div>

                            <input
                                type="text"
                                placeholder="Search by Job ID or Job Title..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--ds-border)', background: 'var(--ds-card)', color: 'var(--ds-text)', fontSize: 12, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }}
                            />

                            <div style={{ border: '1px solid var(--ds-border)', borderRadius: 8, overflow: 'auto', maxHeight: 320, background: 'var(--ds-card)' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                    <thead>
                                        <tr>
                                            <th style={{ ...thStyle, width: 50 }}>Select</th>
                                            <th style={thStyle}>Job ID</th>
                                            <th style={thStyle}>Job Title</th>
                                            <th style={thStyle}>Posted Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {jobPostings.length > 0 ? jobPostings.map(job => (
                                            <tr
                                                key={job.id}
                                                onClick={() => handleJobSelection(job)}
                                                style={{ cursor: 'pointer' }}
                                                onMouseEnter={e => e.currentTarget.style.background = 'var(--ds-table-hover)'}
                                                onMouseLeave={e => e.currentTarget.style.background = ''}
                                            >
                                                <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                    <input
                                                        type="radio"
                                                        name="jobSelection"
                                                        checked={selectedJob?.id === job.id}
                                                        onChange={() => handleJobSelection(job)}
                                                        style={{ accentColor: '#0041C2', cursor: 'pointer' }}
                                                        onClick={e => e.stopPropagation()}
                                                    />
                                                </td>
                                                <td style={tdStyle}><strong style={{ color: '#1A5FD4' }}>{job.job_code || 'N/A'}</strong></td>
                                                <td style={tdStyle}>{job.position_title || 'N/A'}</td>
                                                <td style={{ ...tdStyle, color: 'var(--ds-text2)' }}>{job.created || 'N/A'}</td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan={4} style={{ ...tdStyle, textAlign: 'center', padding: 32, color: 'var(--ds-text2)' }}>
                                                    {searchTerm ? 'No job postings found matching your search.' : 'No job postings available.'}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {totalPages > 1 && (
                                <div style={{ display: 'flex', justifyContent: 'center' }}>
                                    <GlobalPagination
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        onPageChange={p => loadJobPostings(p, searchTerm)}
                                        recordsPerPage={jobsPageSize}
                                        onPageSizeChange={size => { setJobsPageSize(size); loadJobPostings(1, searchTerm, size); }}
                                        totalItems={totalJobs > 0 ? totalJobs : jobPostings.length}
                                        startIndex={jobsStartIndex}
                                        endIndex={jobsEndIndex}
                                        label="total jobs"
                                        size="medium"
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Step 2: Applicant Selection ── */}
                    {step === 2 && !loading && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--ds-text)' }}>Select Applicants to Import</span>
                                <span style={{ padding: '3px 10px', borderRadius: 6, background: 'rgba(0,65,194,0.12)', color: '#1A5FD4', fontSize: 11, fontWeight: 600, border: '1px solid rgba(0,65,194,0.2)' }}>
                                    {totalApplicants > 0 ? totalApplicants : applicants.length} applicants
                                </span>
                            </div>

                            {totalApplicants > 1000 && (
                                <div style={{ padding: '10px 14px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, fontSize: 12, color: '#92400e' }}>
                                    <i className="fas fa-exclamation-triangle" style={{ marginRight: 6 }} />
                                    <strong>Warning:</strong> Showing {totalApplicants.toLocaleString()} applicants. This might include applicants from other jobs. CEIPAL API job filtering may not be working correctly.
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                <input
                                    type="text"
                                    placeholder="Search applicants by name or email..."
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    style={{ flex: 1, minWidth: 180, padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--ds-border)', background: 'var(--ds-card)', color: 'var(--ds-text)', fontSize: 12, fontFamily: 'inherit', outline: 'none' }}
                                />
                                <button
                                    onClick={() => handleSelectPage(true)}
                                    disabled={applicants.length === 0}
                                    style={{ ...footBtn('linear-gradient(135deg,#0041C2,#1A5FD4)', 'none'), opacity: applicants.length === 0 ? 0.5 : 1, padding: '7px 12px', fontSize: 11 }}
                                >
                                    Select Page
                                </button>
                                {totalApplicants > applicants.length && (
                                    <button
                                        onClick={handleSelectAllApplicants}
                                        disabled={totalApplicants === 0 || loading}
                                        style={{ ...footBtn('linear-gradient(135deg,#0041C2,#1A5FD4)', 'none'), opacity: (totalApplicants === 0 || loading) ? 0.5 : 1, padding: '7px 12px', fontSize: 11 }}
                                    >
                                        Select All {totalApplicants}
                                    </button>
                                )}
                                <button
                                    onClick={handleClearAllSelections}
                                    disabled={selectedApplicants.length === 0}
                                    style={{ padding: '7px 12px', borderRadius: 8, border: '1.5px solid var(--ds-border)', background: 'var(--ds-input-bg)', color: 'var(--ds-text2)', fontSize: 11, fontWeight: 600, cursor: selectedApplicants.length === 0 ? 'not-allowed' : 'pointer', fontFamily: 'inherit', outline: 'none', opacity: selectedApplicants.length === 0 ? 0.5 : 1 }}
                                >
                                    Clear All
                                </button>
                            </div>

                            <div style={{ border: '1px solid var(--ds-border)', borderRadius: 8, overflow: 'auto', maxHeight: 300, background: 'var(--ds-card)' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                    <thead>
                                        <tr>
                                            <th style={{ ...thStyle, width: 50 }}>Select</th>
                                            <th style={thStyle}>Name</th>
                                            <th style={thStyle}>Email</th>
                                            <th style={thStyle}>Phone</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {applicants.length > 0 ? applicants.map(applicant => (
                                            <tr
                                                key={applicant.id}
                                                onClick={() => handleApplicantSelection(applicant.id, !selectedApplicants.includes(applicant.id))}
                                                style={{ cursor: 'pointer' }}
                                                onMouseEnter={e => e.currentTarget.style.background = 'var(--ds-table-hover)'}
                                                onMouseLeave={e => e.currentTarget.style.background = ''}
                                            >
                                                <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedApplicants.includes(applicant.id)}
                                                        onChange={e => handleApplicantSelection(applicant.id, e.target.checked)}
                                                        style={{ accentColor: '#0041C2', cursor: 'pointer' }}
                                                        onClick={e => e.stopPropagation()}
                                                    />
                                                </td>
                                                <td style={tdStyle}>
                                                    <strong>{applicant.firstname} {applicant.lastname}</strong>
                                                </td>
                                                <td style={{ ...tdStyle, color: 'var(--ds-text2)' }}>{applicant.email || 'N/A'}</td>
                                                <td style={{ ...tdStyle, color: 'var(--ds-text2)' }}>
                                                    {formatPhoneCompact(applicant.mobile_number || applicant.mobileNumber || applicant.phone) || 'N/A'}
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan={4} style={{ ...tdStyle, textAlign: 'center', padding: 32, color: 'var(--ds-text2)' }}>
                                                    {searchTerm ? 'No applicants found matching your search.' : 'No applicants found for this job.'}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {totalApplicants > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'center' }}>
                                    <GlobalPagination
                                        currentPage={applicantCurrentPage}
                                        totalPages={Math.max(applicantTotalPages, 1)}
                                        onPageChange={p => loadApplicantsForJob(selectedJob?.id, p, searchTerm, applicantsPageSize)}
                                        recordsPerPage={applicantsPageSize}
                                        onPageSizeChange={size => {
                                            const normalized = normalizePageSize(size);
                                            setApplicantsPageSize(normalized);
                                            if (selectedJob?.id) loadApplicantsForJob(selectedJob.id, 1, searchTerm, normalized);
                                        }}
                                        totalItems={totalApplicants > 0 ? totalApplicants : applicants.length}
                                        startIndex={applicantsStartIndex}
                                        endIndex={applicantsEndIndex}
                                        label="total applicants"
                                        size="medium"
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{ padding: '12px 16px', background: 'var(--ds-card)', borderTop: '1px solid var(--ds-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                    <button
                        onClick={handleClose}
                        disabled={importing}
                        style={{ padding: '7px 16px', borderRadius: 8, border: '1.5px solid var(--ds-border)', background: 'var(--ds-input-bg)', color: 'var(--ds-text2)', fontSize: 12, fontWeight: 600, cursor: importing ? 'not-allowed' : 'pointer', fontFamily: 'inherit', outline: 'none', opacity: importing ? 0.6 : 1 }}
                    >
                        Cancel
                    </button>
                    {step === 2 && (
                        <button
                            onClick={handleImport}
                            disabled={selectedApplicants.length === 0 || importing}
                            style={{ ...footBtn('linear-gradient(135deg,#0041C2,#1A5FD4)', '0 2px 8px rgba(0,65,194,0.3)'), opacity: (selectedApplicants.length === 0 || importing) ? 0.6 : 1, minWidth: 160 }}
                        >
                            {importing ? (
                                <><span className="spinner-border spinner-border-sm" role="status" /> Importing...</>
                            ) : (
                                <><i className="fas fa-download" /> Import {selectedApplicants.length} Resume{selectedApplicants.length !== 1 ? 's' : ''}</>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default CeipalImportModal;
